/**
 * TanStack Query hooks for User Attribute Values
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { isApiError } from '@/lib/api-client'
import {
  fetchSubjectAttributes,
  setSubjectAttribute,
  deleteSubjectAttribute,
  fetchAttributeHistory,
} from '@/lib/uas-api'
import { subjectKeys } from '@/hooks/use-subjects'
import type { SetAttributeValueRequest } from '@/types/uas'

export const attributeValueKeys = {
  all: ['uas', 'attribute-values'] as const,
  subject: (subjectId: string) => [...attributeValueKeys.all, subjectId] as const,
  history: (subjectId: string, key: string) =>
    [...attributeValueKeys.subject(subjectId), 'history', key] as const,
}

export function useSubjectAttributes(subjectId: string | undefined) {
  return useQuery({
    queryKey: subjectId
      ? attributeValueKeys.subject(subjectId)
      : ['uas', 'attribute-values', 'disabled'],
    queryFn: () => {
      if (!subjectId) return Promise.reject(new Error('Subject ID is required'))
      return fetchSubjectAttributes(subjectId)
    },
    enabled: !!subjectId,
    staleTime: 10 * 1000,
  })
}

export function useAttributeHistory(
  subjectId: string | undefined,
  attributeKey: string | undefined
) {
  const enabled = !!subjectId && !!attributeKey

  return useQuery({
    queryKey:
      subjectId && attributeKey
        ? attributeValueKeys.history(subjectId, attributeKey)
        : ['uas', 'attribute-values', 'history', 'disabled'],
    queryFn: () => {
      if (!subjectId || !attributeKey) {
        return Promise.reject(new Error('Subject ID and attribute key are required'))
      }
      return fetchAttributeHistory(subjectId, attributeKey)
    },
    enabled,
    staleTime: 10 * 1000,
  })
}

export function useSetAttribute(subjectId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: { key: string } & SetAttributeValueRequest) => {
      const { key, ...rest } = data
      return setSubjectAttribute(subjectId, key, rest)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: attributeValueKeys.subject(subjectId),
      })
      queryClient.invalidateQueries({ queryKey: subjectKeys.detail(subjectId) })
      toast.success('Attribute value updated')
    },
    onError: (error) => {
      const message = isApiError(error) ? error.message : 'An unexpected error occurred'
      toast.error(`Failed to set attribute: ${message}`)
    },
  })
}

export function useDeleteAttribute(subjectId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (key: string) => deleteSubjectAttribute(subjectId, key),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: attributeValueKeys.subject(subjectId),
      })
      toast.success('Attribute value removed')
    },
    onError: (error) => {
      const message = isApiError(error) ? error.message : 'An unexpected error occurred'
      toast.error(`Failed to remove attribute: ${message}`)
    },
  })
}
